import CryptoJS from "crypto-js";
/**
 * receive the localStorage item for the given key
 * @param {string} key
 * @param {*} defaultValue the default value to return if the key is not present in the locale storage
 * @returns object or array if the item was such a thing before storage, otherwise a raw string
 */
export const getStorageItem = (key, defaultValue) => {
    // try to be smart, guess if it was a object or array
    let item = window.localStorage.getItem(key);
    if (!!item && typeof item === "string") {
        // try to parse as js
        // if that fails silently fallback to simple string
        try {
            return JSON.parse(item);
        }
        catch (err) {
            return item;
        }
    }
    else {
        // no such item, return defaultValue if provided
        if (defaultValue)
            return defaultValue;
    }
};
/**
 * removes the given localStorage key
 * @param key
 */
export const clearStorageItem = (key) => window.localStorage.removeItem(key);
/**
 * stores an item under the given localStorage key
 * @param key {string} storage key
 * @param item item to store, either a string or an object/array (which is stringified automatically)
 */
export const setStorageItem = (key, item) => {
    if (typeof item !== "string")
        window.localStorage.setItem(key, JSON.stringify(item));
    else
        window.localStorage.setItem(key, item);
};
/**
 * print localStorage to the console
 */
export const dumpLocalStorage = () => {
    console.info("Contents of LocalStorage");
    for (var k = 0; k < window.localStorage.length; ++k)
        console.info(window.localStorage.key(k) +
            " => " +
            window.localStorage.getItem(window.localStorage.key(k) ?? ""));
};
export const legacyCredentialStorage = {
    getStorageItem: getStorageItem,
    setStorageItem: setStorageItem,
    clearStorageItem: clearStorageItem,
};
export var Method;
(function (Method) {
    Method["get"] = "GET";
    Method["post"] = "POST";
    Method["put"] = "PUT";
    Method["delete"] = "DELETE";
    Method["head"] = "HEAD";
})(Method || (Method = {}));
export var ResponseType;
(function (ResponseType) {
    ResponseType["text"] = "text";
    ResponseType["arraybuffer"] = "arraybuffer";
    ResponseType["blob"] = "blob";
    ResponseType["document"] = "document";
    ResponseType["json"] = "json";
})(ResponseType || (ResponseType = {}));
export var ResultStatus;
(function (ResultStatus) {
    ResultStatus["timeout"] = "timeout";
    ResultStatus["abort"] = "abort";
    ResultStatus["unauthenticated"] = "unauthenticated";
    ResultStatus["unauthorized"] = "unauthorized";
    ResultStatus["nosuccess"] = "nosuccess";
    ResultStatus["error"] = "error";
    ResultStatus["parse"] = "parse";
    ResultStatus["success"] = "success";
})(ResultStatus || (ResultStatus = {}));
export const xhrPromise = (promiseOptions, authenticating) => {
    let xhrOptions = Object.assign({
        success: undefined,
        error: undefined,
    }, promiseOptions);
    let xhrFactory = new XHRFactory(promiseOptions.noAbort);
    let executor = (resolve, reject) => {
        xhrOptions.success = (data, type, xhr, method, url) => resolve({ data: data, type: type, xhr: xhr, method: method, url: url });
        xhrOptions.error = (error, xhr, status) => reject({ error: error, xhr: xhr, status: status });
        _xhr(xhrFactory, xhrOptions, authenticating);
    };
    return new XHRPromise(executor, xhrFactory);
};
export const xhr = (options, authenticating) => {
    let xhrFactory = new XHRFactory();
    _xhr(xhrFactory, options, authenticating);
    return xhrFactory;
};
const _xhr = (xhrFactory, options, authenticating) => {
    let xhrReq = xhrFactory.get();
    options.url =
        options.url +
            (options.cache !== true && options.url.indexOf("_=") == -1
                ? (options.url.indexOf("?") !== -1 ? "&" : "?") + "_=" + Date.now()
                : "");
    const handleProgress = (event, resultStatus) => {
        if (options.progress) {
            let done = event === undefined || xhrReq.readyState === XMLHttpRequest.DONE;
            if (event && event.lengthComputable) {
                let absolute = event.loaded;
                let relative = absolute > 0 ? absolute / event.total : 1;
                options.progress(absolute, relative, done, resultStatus, xhrReq);
            }
            else {
                let absolute = xhrReq.response
                    ? xhrReq.response.byteLength !== undefined
                        ? xhrReq.response.byteLength
                        : xhrReq.response.length !== undefined
                            ? xhrReq.response.length
                            : 0
                    : 0;
                let relative = !done ? (absolute === 0 ? 0 : undefined) : 1;
                options.progress(absolute, relative, done, resultStatus, xhrReq);
            }
        }
    };
    xhrReq.addEventListener("loadstart", (evt) => handleProgress(evt, undefined));
    xhrReq.addEventListener("progress", (evt) => handleProgress(evt, undefined));
    //xhrReq.addEventListener("load", ( evt ) => handleProgress( evt ) );
    //xhrReq.addEventListener("abort", ( evt ) => handleProgress( evt ) );
    //xhrReq.addEventListener("error", ( evt ) => handleProgress( evt ) );
    xhrReq.addEventListener("timeout", (evt) => {
        handleProgress(evt, ResultStatus.timeout);
        if (options.error) {
            options.error(ResultStatus.timeout, xhrReq, 999);
        }
    });
    xhrReq.addEventListener("loadend", (evt) => {
        if (xhrReq.readyState === XMLHttpRequest.DONE && xhrReq.status > 0) {
            if (xhrReq.status >= 200 && xhrReq.status < 300) {
                //completed in success
                let response = xhrReq.response;
                if (evt.loaded > 0 && xhrReq.response === null) {
                    //xhrReq.responseType response type does not match the content send back by the server
                    handleProgress(evt, ResultStatus.parse);
                    if (options.error) {
                        options.error(ResultStatus.parse, xhrReq, xhrReq.status);
                    }
                }
                else {
                    let responseType = ResponseType.text;
                    if (xhrReq.responseType === "" || xhrReq.responseType === "text") {
                        responseType = ResponseType.text;
                    }
                    else if (xhrReq.responseType === "arraybuffer") {
                        responseType = ResponseType.arraybuffer;
                    }
                    else if (xhrReq.responseType === "blob") {
                        responseType = ResponseType.blob;
                    }
                    else if (xhrReq.responseType === "document") {
                        responseType = ResponseType.document;
                        if (response !== null &&
                            response.head &&
                            response.body &&
                            response.head.innerHTML === "") {
                            //most likely a DocumentFragment so unwrap and return what the server sent
                            response = response.body.firstChild;
                        }
                    }
                    else if (xhrReq.responseType === "json") {
                        responseType = ResponseType.json;
                    }
                    handleProgress(evt, ResultStatus.success);
                    if (options.success) {
                        options.success(response, responseType, xhrReq, options.method, options.url);
                    }
                }
            }
            else {
                // completed in http status error
                if (xhrReq.status === 401 || xhrReq.status === 403) {
                    //not authenticated OR not authorized (can happen when authentication was optional and credentials went stale)
                    if (options.digester &&
                        !authenticating === true &&
                        options.digester.onAuthenticate(xhrReq.getResponseHeader("WWW-Authenticate"))) {
                        //a digester is present AND this was the first try AND the WWW-Authenticate header was successfully processed by the digester -> try once more
                        xhrFactory.refresh();
                        _xhr(xhrFactory, options, true);
                    }
                    else {
                        handleProgress(evt, xhrReq.status === 401
                            ? ResultStatus.unauthenticated
                            : ResultStatus.unauthorized);
                        if (options.error) {
                            options.error(xhrReq.status === 401
                                ? ResultStatus.unauthenticated
                                : ResultStatus.unauthorized, xhrReq, xhrReq.status);
                        }
                    }
                }
                else {
                    handleProgress(evt, ResultStatus.nosuccess);
                    if (options.error) {
                        options.error(ResultStatus.nosuccess, xhrReq, xhrReq.status);
                    }
                }
            }
        }
        else {
            //aborted
            handleProgress(evt, ResultStatus.abort);
            if (options.error) {
                options.error(ResultStatus.abort, xhrReq, 0);
            }
        }
    });
    if (options.mimeType) {
        xhrReq.overrideMimeType(options.mimeType);
    }
    try {
        xhrReq.open(options.method, (options.host !== undefined ? options.host : "") + options.url); //always asynchronous
        xhrReq.timeout =
            options.timeout != null && options.timeout > 0 ? options.timeout : 0;
        xhrReq.responseType = options.responseType
            ? options.responseType
            : ResponseType.text;
        if (options.digester) {
            options.digester.setAuthorization(xhrReq, options);
        }
        if (options.data && options.data.mimeType) {
            xhrReq.setRequestHeader("Content-Type", options.data.mimeType);
            //            xhrReq.setRequestHeader( 'Accept', options.data.mimeType );
        }
        if (options.accept) {
            xhrReq.setRequestHeader("Accept", options.accept);
        }
        xhrReq.send(options.data && options.data.entity ? options.data.entity : undefined);
    }
    catch (x) {
        handleProgress(undefined, ResultStatus.error);
        if (options.error) {
            options.error(ResultStatus.error, xhrReq, x);
        }
    }
};
export class XHRPromise extends Promise {
    xhrAborter;
    constructor(executor, xhrAborter) {
        super(executor);
        this.xhrAborter = xhrAborter;
        //    this.xhrAborter = xhrAborter;
    }
    canAbort() {
        return this.xhrAborter.canAbort();
    }
    abort() {
        this.xhrAborter.abort();
    }
}
class XHRFactory {
    noAbort;
    xhrReq;
    constructor(noAbort) {
        this.noAbort = noAbort;
        this.xhrReq = new XMLHttpRequest();
    }
    refresh() {
        this.xhrReq = new XMLHttpRequest();
    }
    get() {
        return this.xhrReq;
    }
    canAbort() {
        return this.noAbort !== true;
    }
    abort() {
        if (this.canAbort())
            this.xhrReq.abort();
    }
}
export class SHA256Digester {
    credentialsStorage;
    ENCRYPTED_PASSWORD = "+++encrypted+++";
    WWWAuthenticate = null;
    credentials = undefined;
    guestCredentials = undefined;
    password = undefined;
    nc = 0;
    clientKey = "" + new Date().getTime();
    WWWAuthentication = undefined;
    constructor(credentialsStorage) {
        this.credentialsStorage = credentialsStorage;
    }
    clear = (credentials) => {
        this.WWWAuthentication = undefined;
        this.nc = 0;
        this.setCredentials(credentials);
    };
    setCredentials = (credentials) => {
        this.credentials = credentials ? credentials : this.guestCredentials;
        if (this.credentialsStorage) {
            if (this.credentials && this.credentials.length > 0) {
                this.credentialsStorage.setStorageItem("ucKey", this.credentials);
            }
            else {
                this.credentialsStorage.clearStorageItem("ucKey");
            }
        }
    };
    equalsCredentials = (credentials) => credentials === this.credentials;
    reloadCredentials = () => {
        let credentials = this.credentialsStorage
            ? this.credentialsStorage.getStorageItem("ucKey")
            : undefined;
        this.credentials =
            credentials && credentials.length > 0
                ? credentials
                : this.guestCredentials;
    };
    getCredentials = () => {
        return this.credentials;
    };
    setUserName = (userName) => {
        if (this.credentials && this.credentials.indexOf(":") !== -1) {
            this.setCredentials(userName + this.credentials.substring(this.credentials.indexOf(":")));
        }
        else {
            this.setCredentials(userName + "::");
        }
    };
    setPassword = (password) => {
        if (this.credentials && this.credentials.indexOf(":") !== -1) {
            this.setCredentials(this.credentials.substring(0, this.credentials.indexOf(":")) +
                "::" +
                password);
        }
        else {
            this.setCredentials("::" + password);
        }
    };
    getUserName = () => {
        return this._extractUserName(this.credentials);
    };
    _extractUserName = (credentials) => {
        if (credentials && credentials.indexOf(":") !== -1) {
            return credentials.substring(0, credentials.indexOf(":"));
        }
        else {
            return undefined;
        }
    };
    _getHA1 = (credentials) => {
        if (this.WWWAuthenticate && credentials) {
            return CryptoJS.SHA256(credentials.replace("::", ":" + this.WWWAuthenticate.realm + ":")).toString(CryptoJS.enc.Hex);
        }
        else {
            return undefined;
        }
    };
    _getHA2 = (method, uri) => {
        var decodedURI = decodeURI(uri);
        return CryptoJS.SHA256(method + ":" + decodedURI).toString(CryptoJS.enc.Hex);
    };
    _setWWWAuthenticate = (WWWAuthenticateHeader) => {
        if (WWWAuthenticateHeader && WWWAuthenticateHeader.length > 0) {
            const firstSpaceIndex = WWWAuthenticateHeader.indexOf(" ");
            if (firstSpaceIndex == -1)
                return false;
            const challengeScheme = WWWAuthenticateHeader.substring(0, firstSpaceIndex);
            if (challengeScheme === "SHA256Digest") {
                let parsedWWWAuthenticateHeader = {
                    challengeScheme: challengeScheme,
                };
                const parameters = WWWAuthenticateHeader.substring(firstSpaceIndex).split(",");
                for (let i = 0; i < parameters.length; i++) {
                    const keyValue = parameters[i].split("=");
                    const key = keyValue[0].replace(" ", "");
                    const value = keyValue[1].charAt(0) == '"'
                        ? keyValue[1].substring(1, keyValue[1].length - 1)
                        : keyValue[1];
                    parsedWWWAuthenticateHeader[key] = value;
                }
                this.WWWAuthenticate = parsedWWWAuthenticateHeader;
                return true;
            }
            else {
                throw new Error("Challenge scheme SHA256Digest is supported only.");
            }
        }
        else {
            // this.WWWAuthenticate=null;
            return false;
        }
    };
    _getNC = () => {
        const ncNumber = this.nc;
        var ncStr = ncNumber.toString(16);
        while (ncStr.length < 8)
            ncStr = "0" + ncStr;
        return ncStr;
    };
    _getCNONCE = () => {
        var currentTimeMS = "" + new Date().getTime();
        return CryptoJS.SHA256(currentTimeMS + ":" + this.nc + ":" + this.clientKey).toString(CryptoJS.enc.Hex);
    };
    _getResponse = (method, uri, cnonce, nc, credentials) => {
        const ha1 = this._getHA1(credentials);
        const decodedUri = decodeURIComponent(uri);
        const ha2 = this._getHA2(method, decodedUri);
        if (this.WWWAuthenticate) {
            if (this.WWWAuthenticate.qop != null &&
                (this.WWWAuthenticate.qop == "auth" ||
                    this.WWWAuthenticate.qop == "auth-int")) {
                return CryptoJS.SHA256(ha1 +
                    ":" +
                    this.WWWAuthenticate.nonce +
                    ":" +
                    nc +
                    ":" +
                    cnonce +
                    ":" +
                    this.WWWAuthenticate.qop +
                    ":" +
                    ha2).toString(CryptoJS.enc.Hex);
            }
            else {
                return CryptoJS.SHA256(ha1 + ":" + this.WWWAuthenticate.nonce + ":" + ha2).toString(CryptoJS.enc.Hex);
            }
        }
        else {
            return undefined;
        }
    };
    _canAuthorize = (credentials) => {
        return (!!this.WWWAuthenticate &&
            !!credentials &&
            credentials.length > 0 &&
            credentials.indexOf("::") > 0 &&
            credentials.indexOf("::") < credentials.length - 2);
    };
    _setAuthorization(xmlHttpRequest, method, uri, credentials) {
        if (this.WWWAuthenticate && this._canAuthorize(credentials)) {
            this.nc++;
            const cnonce = this._getCNONCE();
            const nc = this._getNC();
            const response = this._getResponse(method, uri, cnonce, nc, credentials);
            var authorization = this.WWWAuthenticate.challengeScheme +
                ' username="' +
                this._extractUserName(credentials) +
                '", realm="' +
                this.WWWAuthenticate.realm +
                '", nonce="' +
                this.WWWAuthenticate.nonce +
                '", uri="' +
                uri +
                '", algorithm=' +
                this.WWWAuthenticate.algorithm +
                ', response="' +
                response +
                '", qop=' +
                this.WWWAuthenticate.qop +
                ", nc=" +
                nc +
                ', cnonce="' +
                cnonce +
                '"';
            xmlHttpRequest.setRequestHeader("Authorization", authorization);
            return true;
        }
        else {
            return false;
        }
    }
    onAuthenticate = (WWWAuthenticateHeader) => {
        return this._setWWWAuthenticate(WWWAuthenticateHeader);
    };
    setAuthorization = (xhr, options) => {
        this._setAuthorization(xhr, options.method, options.url, options.credentials ? options.credentials : this.credentials);
    };
}
